import { skipToken } from '@reduxjs/toolkit/dist/query';
import { convert } from 'html-to-text';
import { Delta } from 'quill';
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';
import { Capabilities } from 'src/types/capabilities';
import { Id } from 'src/types/common';
import { TableNavigator } from 'src/types/hooks';
import { Member, Profile } from 'src/types/user';

/**
 * Unique values in array
 */
export const unique = <T>(values: T[]) => [...new Set(values)];

/**
 * Array of given length with given value
 */
export const repeated = <T>(length: number | undefined, value: T) =>
  length ? Array.from({ length }).map(() => value) : [];

/**
 * Array of given length with value from callback
 */
export const repeat = <T>(
  length: number | undefined,
  callback: (i: number) => T
) => (length ? Array.from({ length }).map((_, i) => callback(i)) : []);

// transpose an array type (for zip() function)
export type Zip<T extends unknown[][]> = {
  [K in keyof T]: T[K] extends (infer U)[] ? U : never;
};

/**
 * Stack multiple arrays (i.e. for parallel iteration)
 */
export const zip = <T extends unknown[][]>(...arrays: [...T]): Zip<T>[] =>
  repeat(
    Math.max(...arrays.map((array) => array.length)),
    (i) => arrays.map((array) => array[i]) as Zip<T>
  );

/**
 * Insert a separator between the array items
 */
export const interspense = <T, U>(array: T[], separator: U): (T | U)[] =>
  array.flatMap((item, i) =>
    i < array.length - 1 ? [item, separator] : [item]
  );

/**
 * Compare sets for equal elements
 */
export const compareSets = <T>(a?: Set<T>, b?: Set<T>) =>
  (a === undefined && b === undefined) ||
  (a && b && a.size === b.size && [...a].every((value) => b.has(value)));

/**
 * Unique values in array
 */
export const uniqueSet = <T>([a, ...b]: Set<T>[]) =>
  b.every((x) => compareSets(x, a)) ? a : undefined;

export const unionSet = <T>(sets: Set<T>[]) =>
  sets.reduce((union, set) => new Set([...union, ...set]), new Set<T>());

/**
 * Remove undefined from array and convert type
 */
export function filterUndefined<T>(array: (T | undefined)[]): T[] {
  return array.filter((a) => a !== undefined) as T[];
}

/**
 * Map array and remove undefined (or false for syntactic sugar)
 */
export function filteredMap<T extends object, U extends object>(
  array: T[],
  fn: (a: T) => U | undefined | false
): U[];
export function filteredMap<T extends object, U extends object>(
  array: T[] | undefined,
  fn: (a: T) => U | undefined | false
): U[] | undefined;
export function filteredMap<T extends object, U extends object>(
  array: T[] | undefined,
  fn: (a: T) => U | undefined | false
): U[] | undefined {
  return (
    array &&
    (array.map(fn).filter((a) => a !== undefined && a !== false) as U[])
  );
}

/**
 * Join strings (exclude empty strings)
 */
export const joinFiltered = (
  parts: (string | undefined)[],
  separator?: string
): string => parts.filter((part) => !!part?.length).join(separator);

/**
 * Minimum value allowing undefined
 */
export function filteredMin(...values: (number | undefined)[]) {
  const valid = filterUndefined(values);
  return valid.length === 0 ? undefined : Math.min(...valid);
}

/**
 * Maximum value allowing undefined
 */
export function filteredMax(...values: (number | undefined)[]) {
  const valid = filterUndefined(values);
  return valid.length === 0 ? undefined : Math.max(...valid);
}

/**
 * Minimum value on a given key
 */
export const selectMin = <T, U extends keyof T>(
  array: T[] | undefined,
  key: U
): number | undefined =>
  filteredMin(...(array?.map((item) => item[key] as number | undefined) ?? []));

/**
 * Maximum value on a given key
 */
export const selectMax = <T, U extends keyof T>(
  array: T[] | undefined,
  key: U
): number | undefined =>
  filteredMax(...(array?.map((item) => item[key] as number | undefined) ?? []));

/**
 * Split list depending on a predicate
 */
export const partition = <T>(
  array: T[],
  predicate: (item: T) => boolean
): [T[], T[]] => {
  return array.reduce<[T[], T[]]>(
    ([pass, fail], item) => {
      if (predicate(item)) pass.push(item);
      else fail.push(item);
      return [pass, fail];
    },
    [[], []]
  );
};

/**
 * Generate a new temporary id for the given set (negative)
 */
export const temporaryId = (items: { id?: number }[]) =>
  Math.min(0, ...items.map(({ id }) => id || 0)) - 1;

/**
 * Generate random integer
 */
export const randomInt = (min: number, max: number): number => {
  min = Math.ceil(min);
  max = Math.floor(max);

  return Math.floor(Math.random() * (max - min + 1)) + min;
};

/**
 * Shorthand for asynchronous wait
 */
export const wait = (time: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, time));

/**
 * Simplifies usage of RTK query skip token for queries with id
 */
export const skipWithoutId = <T extends { id?: Id }>({ id, ...data }: T) =>
  id === undefined ? skipToken : { id, ...data };

/**
 * Provide a value only if condition is true (syntactic sugar)
 */
export const onlyWhen = <T>(value: T, condition: boolean) =>
  condition ? value : undefined;

/**
 * Replace empty string with null (for API calls)
 */
export function nullify(value: string | null): string | null;
export function nullify(
  value: string | null | undefined
): string | null | undefined;
export function nullify(
  value: string | null | undefined,
  force: true
): string | null;
export function nullify(
  value: string | null | undefined,
  force = false
): string | null | undefined {
  if (value === undefined && !force) return undefined;
  return value?.length ? value : null;
}

/**
 * Provide value as array (i.e. from configuration files)
 */
export function asArray<T>(value: T | T[]): T[] {
  return Array.isArray(value) ? value : [value];
}

/**
 * Insert or remove an element from an array
 */
export const arrayToggle = <T>(array: T[], item: T, state?: boolean): T[] => {
  const except = array.filter((i) => i !== item);
  const include = state ?? !array.includes(item);

  return include ? [...except, item] : except;
};

/**
 * Await a promise and return a fallback on error
 */
export const awaitWithFallback = <T, U>(
  awaitable: Promise<T>,
  fallback: U
): Promise<T | U> =>
  awaitable.catch((error) => {
    console.warn(error);
    return fallback;
  });

interface Unwrappable<T> {
  unwrap(): Promise<T>;
}

/**
 * Unwrap an RTK query response and return a fallback on error
 */
export const unwrapWithFallback = <T, U>(
  unwrappable: Unwrappable<T>,
  fallback: U
): Promise<T | U> => awaitWithFallback(unwrappable.unwrap(), fallback);

/**
 * Return a search param and ensure numeric type
 */
export const numericSearchParam = (
  params: URLSearchParams,
  key: string,
  fallback: number
) => {
  const value = params.get(key);
  if (value === null) return fallback;
  return isNaN(+value) ? fallback : +value;
};

/**
 * Fast hash function for strings (DJB2 algorithm)
 */
export const stringHash = (str: string): number => {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) + hash + str.charCodeAt(i);
  }

  return hash >>> 0;
};

/**
 * Check if an id is a micro id (obfuscating the numeric id)
 */
export const isMicroId = (id: Id | undefined): id is string =>
  typeof id === 'string' && id.startsWith('I');

/**
 * Default pagination params for endpoints
 */
export const defaultPagination = (
  navigator: TableNavigator<{ query: string | null }>,
  defaultSort?: string[]
) => ({
  search: navigator.filters.query?.length ? navigator.filters.query : undefined,
  page: navigator.page,
  size: navigator.rowsPerPage,
  sort: navigator.sort ? [navigator.sort] : defaultSort
});

/**
 * Validate a dynamic capability (which might contain a path that is not defined)
 */
export const hasCapability = (
  capabilities: Capabilities,
  capability: string
): boolean => {
  // capability path is defined (return plain value)
  if (capability in capabilities)
    return capabilities[capability as keyof Capabilities];
  // capability path is not defined (check parent capability)
  if (capability.includes('.'))
    return hasCapability(capabilities, capability.split('.')[0]);
  // capability is not defined (not granted)
  return false;
};

/**
 * Convert binary string to blob
 */
export const binaryToBlob = (binaryString: string, type: string) => {
  const uint8Array = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    uint8Array[i] = binaryString.charCodeAt(i);
  }

  return new Blob([uint8Array], { type });
};

/**
 * Download the given blob and assign the file given name
 */
export const namedDownload = (blob: Blob | MediaSource, name: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = name;
  link.click();

  // clean up the object URL to free resources
  window.URL.revokeObjectURL(url);
};

/**
 * HACK: apply list styles in order to match Quill
 */
const htmlListStyles = ['decimal', 'lower-alpha', 'lower-roman'];

const injectHtmlListStyle = (html: string): string => {
  let depth = 0;

  return html.replace(/<\/?ol[\s>]/g, (match) => {
    // closing </ol> tag (step back)
    if (match.startsWith('</')) {
      depth--;
      return match;
    }

    // opening <ol> tag (inject the list style for the current depth)
    const style = htmlListStyles[depth % htmlListStyles.length];
    depth++;
    return `<ol style="list-style-type: ${style};">`;
  });
};

/**
 * Convert Quill delta to plain HTML
 */
export const deltaToHtml = (delta: string | Delta | undefined) => {
  if (delta === undefined) return { html: '', text: '' };
  if (typeof delta === 'string') delta = JSON.parse(delta) as Delta;

  // ignore empty delta
  if (!delta.ops) return { html: '', text: '' };

  const converter = new QuillDeltaToHtmlConverter(delta.ops, {});
  const html = injectHtmlListStyle(converter.convert());

  return { html, text: convert(html) };
};

/**
 * Extract file extension
 */
export const getExtension = (name: string, type?: string) => {
  if (type === 'folder') return undefined;
  // extract extension from file name
  return name.split('.').pop();
};

/**
 * Get a member's companies as array (independent of type)
 *
 * Use sparsely, as this circumvents the enforced conditional handling
 * of different user type (personal, company), which will usually result
 * in a better UI/UX experience.
 */
export const getCompanies = (item: Member | Profile) => {
  if (item.type === 'personal') return item.companies;
  if (item.company) return [item.company];
  return undefined;
};
